import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useMyCompanies } from '../../hooks/useMyCompanies'
import { useToast } from '../../hooks/useToast'
import type { LinkedFilial } from '../../hooks/useSeparation'

/**
 * Vínculo matriz↔filial. A empresa ativa é tratada como matriz; o admin
 * vincula como filial uma empresa da qual ele TAMBÉM é membro (validação
 * reforçada na RPC link_filial). Usado pela consolidação do Painel BX e pela
 * lista de separação.
 */
export default function FiliaisSettings() {
  const { companies, activeCompanyId } = useMyCompanies()
  const { notify } = useToast()
  const [filiais, setFiliais] = useState<LinkedFilial[]>([])
  const [selected, setSelected] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('list_linked_filiais')
    if (error) { notify('error', error.message); return }
    setFiliais((data ?? []) as LinkedFilial[])
  }, [notify])

  useEffect(() => { load() }, [load])

  // Candidatas: empresas que o usuário acessa, exceto a ativa e as já vinculadas.
  const linkedIds = new Set(filiais.map(f => f.filial_company_id))
  const candidates = companies.filter(c =>
    c.companyId !== activeCompanyId && !linkedIds.has(c.companyId)
  )

  async function handleLink() {
    if (!selected) return
    setBusy(true)
    const { error } = await supabase.rpc('link_filial', { p_filial: selected })
    setBusy(false)
    if (error) { notify('error', error.message); return }
    notify('success', 'Filial vinculada.')
    setSelected('')
    load()
  }

  async function handleUnlink(filialId: string, name: string) {
    if (!confirm(`Desvincular a filial "${name}"?`)) return
    const { error } = await supabase.rpc('unlink_filial', { p_filial: filialId })
    if (error) { notify('error', error.message); return }
    notify('success', 'Filial desvinculada.')
    load()
  }

  return (
    <section className="bg-surface border border-line rounded-xl p-6 max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink mb-1">Filiais vinculadas</h2>
        <p className="text-sm text-ink-faint">
          Vincule empresas como filiais desta empresa (matriz). A necessidade de compra das
          filiais é somada no Painel BX e gera as listas de separação. Você só pode vincular
          empresas às quais já tem acesso.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          className="bg-paper border border-line rounded px-3 py-2 text-ink text-sm min-w-[240px]"
        >
          <option value="">Selecione uma empresa...</option>
          {candidates.map(c => (
            <option key={c.companyId} value={c.companyId}>{c.name}</option>
          ))}
        </select>
        <button
          onClick={handleLink}
          disabled={busy || !selected}
          className="px-4 py-2 bg-base-600 hover:bg-base-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg"
        >
          {busy ? 'Vinculando...' : 'Vincular filial'}
        </button>
      </div>
      {candidates.length === 0 && (
        <p className="text-xs text-ink-faint">
          Não há outras empresas disponíveis. Para vincular uma filial, você precisa ter acesso a ela
          (peça para ser adicionado como usuário da filial).
        </p>
      )}

      <div className="border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sunken text-ink-muted">
            <tr>
              <th className="text-left px-4 py-2">Filial</th>
              <th className="text-right px-4 py-2">Ação</th>
            </tr>
          </thead>
          <tbody>
            {filiais.map(f => (
              <tr key={f.filial_company_id} className="border-t border-line text-ink-muted">
                <td className="px-4 py-2">{f.filial_name}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => handleUnlink(f.filial_company_id, f.filial_name)}
                    className="text-xs px-3 py-1 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded text-danger"
                  >
                    Desvincular
                  </button>
                </td>
              </tr>
            ))}
            {filiais.length === 0 && (
              <tr><td colSpan={2} className="px-4 py-6 text-center text-ink-faint">Nenhuma filial vinculada.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}
